import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import StatusBadge from './StatusBadge';

const AssetDataGrid = ({
  assets = [],
  selectedAssets = [],
  onSelectionChange,
  onAssetClick,
  onBulkAction
}) => {
  const [selectAll, setSelectAll] = useState(false);

  const handleSelectAll = () => {
    const newSelected = selectAll ? [] : assets.map(asset => asset.id);
    setSelectAll(!selectAll);
    onSelectionChange(newSelected);
  };

  const handleSelectAsset = (assetId) => {
    const newSelected = selectedAssets.includes(assetId)
      ? selectedAssets.filter(id => id !== assetId)
      : [...selectedAssets, assetId];
    onSelectionChange(newSelected);
    setSelectAll(newSelected.length === assets.length);
  };

  if (!assets || assets.length === 0) {
    return (
      <div className="text-center py-12 bg-card rounded-lg border border-border">
        <Icon name="Package" size={48} className="mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">No Assets Found</h3>
        <p className="text-muted-foreground">Try adjusting your filters or add a new asset.</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Bulk Action Bar */}
      {selectedAssets.length > 0 && (
        <div className="bg-primary/10 p-4 flex items-center justify-between border-b border-border">
          <span className="text-sm font-medium">
            {selectedAssets.length} asset{selectedAssets.length !== 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onBulkAction('status')}
              iconName="RefreshCw"
              iconPosition="left"
            >
              Change Status
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onBulkAction('export')}
              iconName="Download"
              iconPosition="left"
            >
              Export
            </Button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={selectAll && assets.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-border"
                />
              </th>
              <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">
                Asset ID
              </th>
              <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">
                Asset Name
              </th>
              <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">
                Model
              </th>
              <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">
                Type
              </th>
              <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">
                Owner
              </th>
              <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">
                Department
              </th>
              <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">
                Location
              </th>
              <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">
                Status
              </th>
              <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">
                Warranty
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {assets.map((asset) => {
              // Safely extract owner name - handle both object and ID cases
              let ownerName = 'Unassigned';
              if (asset.owner) {
                // If owner is an object with name property
                ownerName = typeof asset.owner === 'object' ? (asset.owner.name || 'Unassigned') : asset.owner;
              } else if (asset.owner_id) {
                ownerName = `Owner ID: ${asset.owner_id}`;
              }

              // Safely extract category name
              let categoryName = 'Unknown';
              if (asset.category) {
                categoryName = typeof asset.category === 'object' ? (asset.category.name || 'Unknown') : asset.category;
              } else if (asset.asset_category_id) {
                categoryName = `Category ID: ${asset.asset_category_id}`;
              }

              // Safely extract warranty info
              let warrantyStatus = 'N/A';
              if (asset.warranty_expiration) {
                const warrantyDate = new Date(asset.warranty_expiration);
                const today = new Date();
                const daysLeft = Math.ceil((warrantyDate - today) / (1000 * 60 * 60 * 24));
                if (daysLeft < 0) {
                  warrantyStatus = 'Expired';
                } else if (daysLeft < 30) {
                  warrantyStatus = `${daysLeft} days left`;
                } else {
                  warrantyStatus = `${Math.floor(daysLeft / 30)} months left`;
                }
              }

              return (
                <tr
                  key={asset.id}
                  onClick={() => onAssetClick(asset)}
                  className="hover:bg-muted/30 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedAssets.includes(asset.id)}
                      onChange={() => handleSelectAsset(asset.id)}
                      className="rounded border-border"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm font-semibold text-primary">{asset.asset_id || asset.id}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-sm text-foreground">
                      {asset.name || (asset.brand ? `${asset.brand} ${asset.model || ''}`.trim() : (asset.model || categoryName || 'N/A'))}
                    </div>
                    {asset.serial_number && (
                      <span className="text-xs text-muted-foreground font-mono block">SN: {asset.serial_number}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-foreground">
                      {asset.model || 'N/A'}
                    </div>
                    {asset.brand && (
                      <span className="text-xs text-muted-foreground block">{asset.brand}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm">{categoryName}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm">{ownerName}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm">{asset.department || 'N/A'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm">{asset.location || 'N/A'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={asset.status} />
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-sm ${warrantyStatus === 'Expired' ? 'text-red-600' : ''}`}>
                      {warrantyStatus}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AssetDataGrid;